import { defineStore } from 'pinia'
import {useAuthUserStore} from "@/stores/auth";
import ProjectService from "@/services/project/ProjectService";
import CurriculumService from "@/services/project/CurriculumService";

const emptyProject = () => ({
    academy_id: null,
    created_at: '',
    delete_at: '',
    enrol_configuration: {
        time_start_value: 0,
        time_start_unit: '',
        time_end_value: 0,
        time_end_unit: ''
    },
    products: null,
    product_type_id: null,
    sections: null,
    status: '',
    title: '',
    updated_at: '',
    uuid: '',
})

// El backend no ordena secciones ni lecciones: se ordenan aquí por `order`.
const byOrder = (a, b) => (a.order ?? 0) - (b.order ?? 0)
const sortCurriculum = (sections) => (sections ?? [])
    .slice()
    .sort(byOrder)
    .map((section) => ({...section, lessons: (section.lessons ?? []).slice().sort(byOrder)}))

// Orden del currículo sin datos: [{ uuid, lessons: [uuid, …] }]. Sirve para comparar y deshacer.
const curriculumOrderOf = (sections) => (sections ?? []).map((section) => ({
    uuid: section.uuid,
    lessons: (section.lessons ?? []).map((lesson) => lesson.uuid)
}))

export const useProjectsStore = defineStore('proyect', {
    state: () => ({
        project: emptyProject(),
        loading: false,
    }),
    actions: {
        //Getters
        getProject(projectUuid)
        {
            // Evita mostrar el curso anterior mientras se carga otro distinto.
            if (this.project.uuid !== projectUuid) {
                this.project = emptyProject()
            }

            this.loading = true
            return useAuthUserStore().getApiClient.get('/projects/' + projectUuid, {})
                .then((response) => {
                    this.setProjects(response.data)
                })
                .finally(() => {
                    this.loading = false
                })
        },
        setProjectGeneral() {
            return ProjectService.put({
                uuid: this.project.uuid,
                title: this.project.title,
            })
        },
        //=============================================================
        //                          SECTIONS
        //=============================================================
        // Crea la sección por la API v2 y la añade al final sin recargar el proyecto (FE-060).
        addSection(title) {
            return CurriculumService.createSection(this.project.uuid, title)
                .then((response) => {
                    const section = {...(response.data?.data ?? response.data), lessons: []}
                    // `push` y no una lista nueva: así no se rehacen los títulos guardados del
                    // currículo y no se pierde un título que se esté editando en otra sección.
                    if (this.project.sections) this.project.sections.push(section)
                    else this.project.sections = [section]
                    return section
                })
        },
        editSection(section) {
            // El backend espera `uuid` y resetea `order` a 1 si no se envía.
            return ProjectService.putSection({
                project_uuid: section.project_uuid,
                uuid: section.uuid,
                title: section.title,
                order: section.order
            })
        },

        //=============================================================
        //                          LESSONS
        //=============================================================
        // Ídem para una lección, al final de su sección (FE-060).
        addLesson(section, title) {
            return CurriculumService.createLesson(this.project.uuid, section.uuid, title)
                .then((response) => {
                    const lesson = response.data?.data ?? response.data
                    if (section.lessons) section.lessons.push(lesson)
                    else section.lessons = [lesson]
                    return lesson
                })
        },

        //=============================================================
        //                    CURRÍCULO (API V2)
        //=============================================================
        // Tras borrar se actualiza el estado local en lugar de recargar todo el proyecto.
        curriculumOrder() {
            return curriculumOrderOf(this.project.sections)
        },
        // Recoloca las secciones y lecciones existentes (sin perder ediciones locales) y recalcula `order`.
        applyCurriculumOrder(order) {
            const sections = this.project.sections ?? []
            const sectionsByUuid = Object.fromEntries(sections.map((section) => [section.uuid, section]))
            const lessonsByUuid = Object.fromEntries(sections.flatMap((section) => section.lessons ?? []).map((lesson) => [lesson.uuid, lesson]))
            const knownOrder = order.filter((item) => sectionsByUuid[item.uuid])

            this.project.sections = knownOrder.map((item) => sectionsByUuid[item.uuid])
            this.project.sections.forEach((section, index) => {
                section.order = index + 1
                section.lessons = knownOrder[index].lessons.filter((uuid) => lessonsByUuid[uuid]).map((uuid) => lessonsByUuid[uuid])
                section.lessons.forEach((lesson, lessonIndex) => {
                    lesson.order = lessonIndex + 1
                })
            })
        },
        /*
         * Persisten el orden actual (actualización optimista). Si la API falla se
         * restaura `previousOrder` y se propaga el error para avisar al usuario.
         */
        saveSectionsOrder(previousOrder) {
            this.applyCurriculumOrder(this.curriculumOrder())
            return CurriculumService.reorderSections(this.project.uuid, this.project.sections.map((section) => section.uuid))
                .catch((error) => {
                    this.applyCurriculumOrder(previousOrder)
                    throw error
                })
        },
        saveCurriculumOrder(previousOrder) {
            const order = this.curriculumOrder()
            this.applyCurriculumOrder(order)
            return CurriculumService.syncCurriculum(this.project.uuid, order)
                .catch((error) => {
                    this.applyCurriculumOrder(previousOrder)
                    throw error
                })
        },
        /*
         * Publicación (LMS-064) y vista previa gratuita (LMS-065). Se aplican en
         * local antes de llamar a la API y se revierten si falla.
         */
        setSectionPublished(section, published) {
            const previous = section.published_at
            section.published_at = published ? new Date().toISOString() : null
            return CurriculumService.setSectionPublished(this.project.uuid, section.uuid, published)
                .catch((error) => {
                    section.published_at = previous
                    throw error
                })
        },
        setLessonPublished(section, lesson, published) {
            const previous = lesson.published_at
            lesson.published_at = published ? new Date().toISOString() : null
            return CurriculumService.setLessonPublished(this.project.uuid, section.uuid, lesson.uuid, published)
                .catch((error) => {
                    lesson.published_at = previous
                    throw error
                })
        },
        setLessonFreePreview(section, lesson, isFreePreview) {
            const previous = lesson.is_free_preview
            lesson.is_free_preview = isFreePreview
            return CurriculumService.updateLesson(this.project.uuid, section.uuid, lesson.uuid, {is_free_preview: isFreePreview})
                .catch((error) => {
                    lesson.is_free_preview = previous
                    throw error
                })
        },
        /*
         * Publica o despublica la sección y las lecciones que aún no están en ese
         * estado (FE-065), con las llamadas de una en una. Cada una se revierte sola
         * si falla; devuelve cuántas han fallado.
         */
        async setSectionPublishedWithLessons(section, published) {
            const pending = (section.lessons ?? []).filter((lesson) => !!lesson.published_at !== published)
            const requests = [
                ...(!!section.published_at !== published ? [() => this.setSectionPublished(section, published)] : []),
                ...pending.map((lesson) => () => this.setLessonPublished(section, lesson, published)),
            ]
            let failed = 0
            for (const request of requests) {
                try {
                    await request()
                } catch {
                    failed += 1
                }
            }
            return failed
        },
        // Programación (drip) de una sección (FE-062): se aplica lo que devuelve el backend.
        updateSectionSchedule(section, schedule) {
            return CurriculumService.updateSection(this.project.uuid, section.uuid, {
                drip_days_after_enrolment: schedule.drip_days_after_enrolment,
                available_from: schedule.available_from,
            })
                .then((response) => {
                    const saved = response.data?.data ?? response.data ?? {}
                    const pick = (field) => (field in saved ? saved[field] : schedule[field]) ?? null
                    section.drip_days_after_enrolment = pick('drip_days_after_enrolment')
                    section.available_from = pick('available_from')
                })
        },
        deleteSection(sectionUuid, cascade = false) {
            return CurriculumService.deleteSection(this.project.uuid, sectionUuid, cascade)
                .then(() => {
                    this.project.sections = (this.project.sections ?? []).filter((section) => section.uuid !== sectionUuid)
                })
        },
        /*
         * Duplicar (FE-065, LMS-220): la copia va justo detrás del original, como
         * en el backend, sin recargar el proyecto. Se recalcula `order` en local.
         */
        duplicateLesson(section, lesson) {
            return CurriculumService.duplicateLesson(this.project.uuid, section.uuid, lesson.uuid)
                .then((response) => {
                    const copy = response.data?.data ?? response.data
                    section.lessons.splice(section.lessons.indexOf(lesson) + 1, 0, copy)
                    section.lessons.forEach((item, index) => {
                        item.order = index + 1
                    })
                    return copy
                })
        },
        // La respuesta no trae las lecciones de la copia: se piden aparte.
        async duplicateSection(section) {
            const response = await CurriculumService.duplicateSection(this.project.uuid, section.uuid)
            const copy = response.data?.data ?? response.data
            const lessonsResponse = await CurriculumService.sectionLessons(this.project.uuid, copy.uuid)
            const lessons = (lessonsResponse.data?.data ?? lessonsResponse.data ?? []).slice().sort(byOrder)

            const sections = this.project.sections
            sections.splice(sections.indexOf(section) + 1, 0, {...copy, lessons})
            sections.forEach((item, index) => {
                item.order = index + 1
            })
            return sections.find((item) => item.uuid === copy.uuid)
        },
        renameLesson(sectionUuid, lessonUuid, title) {
            return CurriculumService.updateLesson(this.project.uuid, sectionUuid, lessonUuid, {title})
        },
        deleteLesson(sectionUuid, lessonUuid) {
            return CurriculumService.deleteLesson(this.project.uuid, sectionUuid, lessonUuid)
                .then(() => {
                    const section = (this.project.sections ?? []).find((section) => section.uuid === sectionUuid)
                    if (section) {
                        section.lessons = section.lessons.filter((lesson) => lesson.uuid !== lessonUuid)
                    }
                })
        },

        //=============================================================
        //                          Mutations
        //=============================================================
        setProjects(project) {
            this.project = {
                academy_id: project.academy_id,
                created_at: project.created_at,
                delete_at: project.delete_at,
                enrol_configuration: {
                    time_start_value: (project.enrol_configuration && project.enrol_configuration.time_start_value)? project.enrol_configuration.time_start_value : 0,
                    time_start_unit: (project.enrol_configuration && project.enrol_configuration.time_start_unit)? project.enrol_configuration.time_start_unit : 'hours',
                    time_end_value: (project.enrol_configuration && project.enrol_configuration.time_end_value)? project.enrol_configuration.time_end_value : 0,
                    time_end_unit: (project.enrol_configuration && project.enrol_configuration.time_end_unit)? project.enrol_configuration.time_end_unit : 'hours',
                },
                products: project.products,
                product_type_id: project.product_type_id,
                sections: sortCurriculum(project.sections),
                status: project.status,
                title: project.title,
                updated_at: project.updated_at,
                uuid: project.uuid,
            }
        },
    }

})